import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import {
  getApartmentCoverPhotoUrl,
  getApartmentUrlPath,
} from 'common/apartmentHelpers';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { PARTNER_LISTING_LINKS } from 'common/listingConstants';
import { getUserLandingPage } from 'client/helpers/allowedRoles';

import {
  SCREEN_ID_PARTNER_APARTMENT,
} from 'client/constants/screenIds';

import {
  DATA_TABLE_SOURCE_PARTNER_APARTMENTS,
} from 'common/dataTableConstants';

import SrPartnerListingTourReviews from 'client/components/SrPartnerListingTourReviews';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrPartnerPageFooter from 'client/components/SrPartnerPageFooter';
import SrPartnerBackButton from 'client/components/SrPartnerBackButton';
import SrPartnerListingStatsV2 from 'client/components/SrPartnerListingStatsV2';
import SrPartnerMarketingLinksModal from 'client/components/SrPartnerMarketingLinksModal';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import PartnerActions from 'client/actions/Partner';
import getApartmentLinksAction from 'client/actions/Apartment/getLinks';

import {
  dataTable,
  actionDataTableLoadItems,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

function getDataTableSettings({ apartmentId, partnerSlug }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_APARTMENTS,
    viewId: `by-apartmentId-${apartmentId}`,
    count: 1,
    filter: {
      'apartments.id': parseInt(apartmentId, 10),
    },
    customParams: {
      partnerApartments: {
        partnerSlug: partnerSlug,
      },
    },
  };

  return dataTableSettings;
}

@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_APARTMENT)
@withScrollToTop
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  currentPartnerUser: ['partners', 'currentPartnerUser'],
  partnersBySlug: ['partners', 'bySlug'],
  listing: ['listing'],
  apartmentLinks: ['apartmentLinksByApartmentId', match.params.apartmentId],
  apartmentTourReviews: ['partners', 'apartmentTourReviewsById', match.params.apartmentId],
}))
@dataTable(({ match }) => {
  return getDataTableSettings({
    apartmentId: match.params.apartmentId,
    partnerSlug: match.params.partnerSlug,
  });
})

class PartnerApartmentPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Partner Apartment | Sunroom Rentals',
  })

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    currentPartnerUser: PropTypes.object,
    partnersBySlug: PropTypes.object,
    items: PropTypes.array.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    isTransitioning: PropTypes.bool.isRequired,
    apartmentLinks: PropTypes.array,
    location: PropTypes.object.isRequired,
  }

  static fetchData(match, inject) {
    const partnersBySlug = inject.state.get(['partners', 'bySlug']);
    const apartmentId = parseInt(match.params.apartmentId, 10);
    const promises = [
      PartnerActions.getPartnerListingTourReviews(inject, { apartmentId, forceRefresh: true }),
    ];

    const dataTableSettings = getDataTableSettings({
      apartmentId: match.params.apartmentId,
      partnerSlug: match.params.partnerSlug,
    });

    const apartment = (inject.state.get(['dataTables', dataTableSettings.dataSource, 'items']) || [])
      .find((item) => item.id === apartmentId);

    if (!apartment) {
      actionInitializeDataTableState(inject, dataTableSettings);
      promises.push(actionDataTableLoadItems(inject, dataTableSettings));
    }

    if (!partnersBySlug[match.params.partnerSlug]) {
      promises.push(PartnerActions.loadPartnerBySlug(inject, {
        slug: match.params.partnerSlug,
      }));
    }

    promises.push(getApartmentLinksAction(inject, {
      apartmentId,
    }));

    if (promises.length) {
      return Promise.all(promises);
    }
  }

  constructor(props) {
    super(props);
    this.state = {
      apartmentLinksModalIsOpen: false,
    };
  }

  _onOpenApartmentLinksModal = () => {
    this.setState({
      apartmentLinksModalIsOpen: true,
    });
  }

  _onCloseApartmentLinksModal = () => {
    this.setState({
      apartmentLinksModalIsOpen: false,
    });
  }

  render() {
    const {
      currentUser,
      currentPartnerUser,
      partnersBySlug,
      items,
      apartmentLinks,
      dataTableIsFullyLoaded,
      isTransitioning,
      match,
      apartmentTourReviews,
      dispatch,
      location,
    } = this.props;

    const {
      apartmentLinksModalIsOpen,
    } = this.state;

    const currentPartner = partnersBySlug[match.params.partnerSlug];
    if (isTransitioning && !currentPartner) {
      return null;
    }

    const apartment = items[0];
    const isCurrentPartner = currentPartnerUser && currentPartnerUser.partnerId === currentPartner.id;

    if (dataTableIsFullyLoaded && !apartment) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return (
        <Redirect to={userLandingPage} />
      );
    }

    const apartmentLinksSubset = (apartmentLinks || []).length > 0
      ? (
        [{
          siteName: 'Sunroom Rentals',
          linkUrl: apartment ? getApartmentUrlPath(apartment) : '',
        }].concat(
          PARTNER_LISTING_LINKS
            .map(({ siteName }) => {
              const listingLink = apartmentLinks.find((link) => siteName === link.siteName) || {};

              return {
                siteName: siteName,
                wasReviewed: listingLink.wasReviewed,
                linkUrl: listingLink.linkUrl,
              };
            })
            .filter(({ wasReviewed }) => wasReviewed)
        )
      )
      : null;

    const coverPhotoUrl = getApartmentCoverPhotoUrl(apartment);

    const ApartmentAddress = () => {
      const isSmallScreen = useMediaQuery({
        query: MEDIA_QUERY_SMALL_SCREEN,
      });

      return (
        <div>
          <SrText
            font='greycliff'
            weight='800'
            color='blueGreycliff'
            fontSize={isSmallScreen ? '16px' : '21px'}
            lineHeight={isSmallScreen ? '21px' : '25px'}
          >
            {`${apartment.name},
            ${apartment.apartmentBuildings[0].addressCity},
            ${apartment.apartmentBuildings[0].addressStateCode}
            ${apartment.apartmentBuildings[0].addressZip}`}
          </SrText>
        </div>
      );
    };

    return (
      <div className='SrPartnerPage PartnerListingPage'>
        <SrPartnerPageV2
          backUrl={`/partner/${currentPartner?.slug}`}
          navbarTitle={
            currentPartner?.logoUrl
              ? (
                <img
                  alt='partner logo'
                  src={currentPartner.logoUrl}
                  style={{ height: '44px' }}
                />
              )
              : currentPartner?.displayName
          }
          padding='0'
          contentPadding='0'
          maxWidth='unset'
          navbarMaxWidth='1180px'
          currentUser={currentUser}
          currentPartner={currentPartner}
        >
          {apartment && (
            <>
              <div>
                <div className='SrPartnerListingHeader'>
                  <div
                    className='listingBackgroundImage'
                    style={isTransitioning
                      ? {}
                      // eslint-disable-next-line max-len
                      : (coverPhotoUrl ? { backgroundImage: `url("${coverPhotoUrl}")` } : {})}
                  />
                  <div className='SrPartnerPageContent'>
                    <div className='headerContainer'>
                      <ApartmentAddress />
                      {apartmentLinksSubset && (
                        <SrBox paddingTop='11px'>
                          <span className='blue-link' onClick={this._onOpenApartmentLinksModal}>
                            View marketing pages
                          </span>
                        </SrBox>
                      )}
                    </div>
                  </div>
                  <div className='controlsContainer'>
                    <div className='SrPartnerPageContent'>
                      <div className='controls'>
                        <div>
                          {isCurrentPartner && (
                            <SrPartnerBackButton />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='SrPartnerPageContent'>
                <SrBox margin='0 10px'>
                  <SrPartnerSection title='Stats'>
                    <SrPartnerListingStatsV2
                      apartmentId={apartment.id}
                      sinceDate={momentWithAustinTimezone().subtract(7, 'days').startOf('day').toISOString()}
                    />
                  </SrPartnerSection>
                  <SrPartnerSection title='Tour Feedback'>
                    <SrPartnerListingTourReviews
                      tourReviews={apartmentTourReviews}
                      currentUser={currentUser}
                      partner={currentPartner}
                      dispatch={dispatch}
                      location={location}
                    />
                  </SrPartnerSection>
                  <SrPartnerPageFooter />
                </SrBox>
              </div>
            </>
          )}
          {!dataTableIsFullyLoaded && !apartment && (
            <div className='SrPartnerPageContent'>
              <div className='loading-ellipses' />
            </div>
          )}
          {apartmentLinksModalIsOpen && (
            <SrPartnerMarketingLinksModal
              onClose={this._onCloseApartmentLinksModal}
              links={apartmentLinksSubset}
            />
          )}
        </SrPartnerPageV2>
      </div>
    );
  }
}

PartnerApartmentPage.displayName = 'PartnerApartmentPage';

export default PartnerApartmentPage;
